import * as React from 'react';
import { t } from '@reos/i18n';
import { Camera, CircleAlert } from 'lucide-react';
import type { IntakeContextDto } from '../../lib/field-intake-api.js';
import type { ChecklistState } from '../../lib/field-intake-checklist.js';
import type { OfflineQueue } from '../../lib/field-intake-queue.js';
import type { Recorder, RecorderHandle } from '../../lib/field-intake-recorder.js';
import { ChecklistPanel, checklistKeys } from './ChecklistPanel.js';
import { fieldLabel, formatClock, formatValue } from './values.js';
import { knownSummary } from './StartStep.js';

/**
 * Krok UXV4c bod 3 — nahravani (predloha `Nabor.png`, tablet `NaborTablet.png`).
 *
 * Kazdy usek nahravky, fotka a poznamka jde do OFFLINE FRONTY s casem od
 * zacatku naboru; fronta je posle, az bude sit. Druh nahravky urcil start:
 * se souhlasem `conversation`, bez nej `self_dictation` (diktat sam sobe).
 *
 * Krok NAB1 — TRI ZONY PRACOVNI PLOCHY na desktopu (>= 1024 px): nahravani
 * a poznamky / body k ziskani / co uz je vyplneno. Na mobilu je to tataz
 * jedina kolona jako dosud a seznam bodu je rozbalovaci (`ChecklistPanel`);
 * do NAB1 byl na mobilu jen tip „Ještě se zeptejte" a body nesly odkliknout
 * (nalez R.B. 7. 10. 2026).
 */

export interface CapturedEntry {
  readonly id: string;
  readonly atSeconds: number;
  readonly kind: 'note' | 'photo';
  readonly text: string;
  readonly previewUrl?: string | undefined;
}

const BARS = 28;

/**
 * Od 768 px (tablet a desktop) je zona checklistu rozbalena a jeji rozbalovaci
 * radek schovany (`nabor.css`) — mobil zacina sbaleny, aby nahravani zustalo
 * prvni na obrazovce. Breakpoint MUSI sedet s tim v CSS, jinak by na tabletu
 * byl seznam zavreny bez cehokoli, cim ho otevrit.
 */
function useWideScreen(): boolean {
  const [desktop, setDesktop] = React.useState(() => {
    try {
      return window.matchMedia('(min-width: 768px)').matches;
    } catch {
      // Prohlizec bez `matchMedia` (jsdom v testu) — bere se mobilni chovani.
      return false;
    }
  });
  React.useEffect(() => {
    try {
      const mql = window.matchMedia('(min-width: 768px)');
      if (typeof mql.addEventListener !== 'function') return undefined;
      const onChange = (): void => setDesktop(mql.matches);
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    } catch {
      return undefined;
    }
  }, []);
  return desktop;
}

export function RecordingStep({
  sessionId,
  consent,
  context,
  title,
  recorder,
  queue,
  newId,
  marks,
  onMark,
  onUnmark,
  onDone,
}: {
  readonly sessionId: string;
  readonly consent: boolean;
  readonly context: IntakeContextDto | null;
  readonly title: string;
  readonly recorder: Recorder;
  readonly queue: OfflineQueue;
  readonly newId: () => string;
  /** Krok NAB1 — rucne odskrtnute body (stav drzi obrazovka `/nabor`). */
  readonly marks: ChecklistState;
  /** `atSeconds` = cas od zacatku naboru, stejne jako u poznamky. */
  readonly onMark: (factKey: string, value: unknown, atSeconds: number) => void;
  readonly onUnmark: (factKey: string) => void;
  readonly onDone: () => void;
}): React.JSX.Element {
  const [recording, setRecording] = React.useState(false);
  const [elapsed, setElapsed] = React.useState(0);
  const [levels, setLevels] = React.useState<readonly number[]>(() => new Array<number>(BARS).fill(0));
  const [captured, setCaptured] = React.useState<readonly CapturedEntry[]>([]);
  const [note, setNote] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const handle = React.useRef<RecorderHandle | null>(null);
  const segmentStart = React.useRef(0);
  const baseElapsed = React.useRef(0);
  const startedAt = React.useRef(0);
  const wide = useWideScreen();

  React.useEffect(() => {
    if (!recording) return undefined;
    const timer = window.setInterval(() => {
      setElapsed(baseElapsed.current + (performance.now() - startedAt.current) / 1000);
      const level = handle.current?.level() ?? 0;
      setLevels((prev) => [...prev.slice(1), level]);
    }, 200);
    return () => window.clearInterval(timer);
  }, [recording]);

  const start = async (): Promise<void> => {
    setError(null);
    try {
      handle.current = await recorder.start();
      segmentStart.current = elapsed;
      baseElapsed.current = elapsed;
      startedAt.current = performance.now();
      setRecording(true);
    } catch {
      setError(t('webAdmin.fieldIntake.recording.micDenied', { locale: 'cs' }));
    }
  };

  const stop = async (): Promise<void> => {
    const current = handle.current;
    if (current === null) return;
    handle.current = null;
    setRecording(false);
    const result = await current.stop();
    const total = segmentStart.current + result.durationS;
    baseElapsed.current = total;
    setElapsed(total);
    await queue.enqueue({
      kind: 'recording',
      sessionId,
      id: newId(),
      blob: result.blob,
      recordingKind: consent ? 'conversation' : 'self_dictation',
      durationS: result.durationS,
    });
  };

  const addNote = async (): Promise<void> => {
    const text = note.trim();
    if (text === '') return;
    const id = newId();
    setCaptured((prev) => [...prev, { id, atSeconds: elapsed, kind: 'note', text }]);
    setNote('');
    await queue.enqueue({ kind: 'note', sessionId, id, atSeconds: Math.round(elapsed), text });
  };

  const addPhoto = async (file: File): Promise<void> => {
    const id = newId();
    const previewUrl = typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : undefined;
    setCaptured((prev) => [
      ...prev,
      { id, atSeconds: elapsed, kind: 'photo', text: t('webAdmin.fieldIntake.recording.photo', { locale: 'cs' }), previewUrl },
    ]);
    await queue.enqueue({ kind: 'photo', sessionId, id, blob: file, atSeconds: Math.round(elapsed) });
  };

  const finish = async (): Promise<void> => {
    if (recording) await stop();
    onDone();
  };

  /** Cas v nahravce patri ke znacce stejne jako k poznamce (fronta ho posila dal). */
  const markPoint = (factKey: string, value: unknown): void => onMark(factKey, value, Math.round(elapsed));

  const missing = context?.missing ?? [];
  const points = checklistKeys(missing, marks);
  const tips = missing.filter((key) => marks[key] === undefined).slice(0, 2);
  const summary = knownSummary(context);

  return (
    <div className="rh-fi-split rh-fi-split--rec" data-testid="fi-recording">
      {/*
        Krok NAB1: titulek a „Ukončit a zkontrolovat" stoji NAD zonami (na
        mobilu se neukazuji, tam je titulek v karte a „Hotovo" v ovladani).
        Do NAB1 sedely uvnitr prave kolony, takze na desktopu nebylo kam pridat
        treti zonu.
      */}
      <div className="rh-fi-rechead rh-fi-only-tablet">
        <h2 className="rh-fi-big">{title}</h2>
        <button type="button" className="rh-v4-btn rh-v4-btn--filled" onClick={() => void finish()} data-testid="fi-done-tablet">
          {t('webAdmin.fieldIntake.recording.finishAndReview', { locale: 'cs' })}
        </button>
      </div>

      <div className="rh-fi-col rh-fi-col--talk">
        <div className="rh-fi-kicker rh-fi-only-tablet">{t('webAdmin.fieldIntake.recording.zoneTalk', { locale: 'cs' })}</div>
        <section className="rh-v4-group rh-fi-card rh-fi-only-phone">
          <div className="rh-fi-card__title">{title}</div>
          {summary !== null ? (
            <div className="rh-fi-card__sub">{t('webAdmin.fieldIntake.start.known', { locale: 'cs', values: { facts: summary } })}</div>
          ) : null}
        </section>

        <section className="rh-v4-group rh-fi-rec" data-testid="fi-recorder">
          <div className="rh-fi-rec__time" data-testid="fi-time">
            {formatClock(elapsed)}
          </div>
          <div className={`rh-fi-wave${recording ? ' is-live' : ''}`} aria-hidden="true">
            {levels.map((level, i) => (
              <span key={i} className="rh-fi-wave__bar" style={{ height: `${String(Math.round(8 + level * 52))}px` }} />
            ))}
          </div>
          <div className="rh-fi-rec__state" data-testid="fi-rec-state">
            {recording
              ? consent
                ? t('webAdmin.fieldIntake.recording.liveConsent', { locale: 'cs' })
                : t('webAdmin.fieldIntake.recording.liveSelf', { locale: 'cs' })
              : elapsed > 0
                ? t('webAdmin.fieldIntake.recording.paused', { locale: 'cs' })
                : t('webAdmin.fieldIntake.recording.ready', { locale: 'cs' })}
          </div>
          {!recorder.supported ? (
            <p className="rh-fi-note rh-fi-note--warn">{t('webAdmin.fieldIntake.recording.unsupported', { locale: 'cs' })}</p>
          ) : null}
          {error !== null ? (
            <p className="rh-fi-note rh-fi-note--warn" role="alert">
              {error}
            </p>
          ) : null}
        </section>

        <h2 className="rh-fi-h">{t('webAdmin.fieldIntake.recording.captured', { locale: 'cs' })}</h2>
        <section className="rh-v4-group" data-testid="fi-captured">
          {captured.length === 0 ? (
            <div className="rh-v4-row rh-v4-row--static rh-fi-muted">{t('webAdmin.fieldIntake.recording.capturedEmpty', { locale: 'cs' })}</div>
          ) : (
            captured.map((entry) => (
              <div key={entry.id} className="rh-v4-row rh-v4-row--static" data-testid={`fi-captured-${entry.kind}`}>
                <span className="rh-v4-row__lead rh-fi-time">{formatClock(entry.atSeconds)}</span>
                <span className="rh-v4-row__text">
                  <span className="rh-v4-row__title">{entry.text}</span>
                </span>
                {entry.previewUrl !== undefined ? <img className="rh-fi-thumb" src={entry.previewUrl} alt="" /> : null}
              </div>
            ))
          )}
          <div className="rh-v4-row rh-v4-row--static">
            <input
              className="rh-input rh-fi-input rh-fi-input--grow"
              value={note}
              placeholder={t('webAdmin.fieldIntake.recording.notePlaceholder', { locale: 'cs' })}
              aria-label={t('webAdmin.fieldIntake.recording.notePlaceholder', { locale: 'cs' })}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void addNote();
              }}
              data-testid="fi-note"
            />
            <button type="button" className="rh-v4-btn rh-v4-btn--chip" onClick={() => void addNote()} data-testid="fi-note-add">
              {t('webAdmin.fieldIntake.recording.noteAdd', { locale: 'cs' })}
            </button>
          </div>
        </section>

        {tips.length > 0 ? (
          <section className="rh-fi-tip rh-fi-only-phone" data-testid="fi-tip">
            <CircleAlert size={22} aria-hidden="true" className="rh-fi-tip__icon" />
            <span>
              {t('webAdmin.fieldIntake.recording.askPrefix', { locale: 'cs' })}{' '}
              {tips.map((key, i) => (
                <React.Fragment key={key}>
                  {i > 0 ? ` ${t('webAdmin.fieldIntake.recording.askAnd', { locale: 'cs' })} ` : null}
                  <button type="button" className="rh-fi-tip__item" onClick={() => markPoint(key, undefined)}>
                    {fieldLabel(key).toLowerCase()}
                  </button>
                </React.Fragment>
              ))}
              .
            </span>
          </section>
        ) : null}

        <div className="rh-fi-controls" data-testid="fi-controls">
          <label className="rh-fi-round rh-fi-round--light" aria-label={t('webAdmin.fieldIntake.recording.takePhoto', { locale: 'cs' })}>
            <Camera size={26} aria-hidden="true" />
            <input
              type="file"
              accept="image/*"
              capture="environment"
              className="rh-fi-hidden"
              data-testid="fi-photo"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file !== undefined) void addPhoto(file);
                e.target.value = '';
              }}
            />
          </label>
          <button
            type="button"
            className={`rh-fi-record${recording ? ' is-recording' : ''}`}
            onClick={() => void (recording ? stop() : start())}
            disabled={!recorder.supported}
            aria-label={recording ? t('webAdmin.fieldIntake.recording.stop', { locale: 'cs' }) : t('webAdmin.fieldIntake.recording.record', { locale: 'cs' })}
            data-testid="fi-record"
          >
            <span className="rh-fi-record__inner" aria-hidden="true" />
          </button>
          <button type="button" className="rh-v4-btn rh-v4-btn--plain rh-fi-done" onClick={() => void finish()} data-testid="fi-done">
            {t('webAdmin.fieldIntake.recording.done', { locale: 'cs' })}
          </button>
        </div>
      </div>

      <div className="rh-fi-col rh-fi-col--points" data-testid="fi-points">
        <div className="rh-fi-kicker rh-fi-only-tablet">{t('webAdmin.fieldIntake.recording.zoneChecklist', { locale: 'cs' })}</div>
        <ChecklistPanel keys={points} marks={marks} onMark={markPoint} onUnmark={onUnmark} defaultOpen={wide} />
      </div>

      <div className="rh-fi-col rh-fi-col--facts rh-fi-only-tablet" data-testid="fi-live-facts">
        <div className="rh-fi-kicker">{t('webAdmin.fieldIntake.recording.zoneKnown', { locale: 'cs' })}</div>
        <p className="rh-fi-muted">{t('webAdmin.fieldIntake.recording.fillsItself', { locale: 'cs' })}</p>
        {context !== null && context.known.length > 0 ? (
          <>
            <h3 className="rh-fi-h">{t('webAdmin.fieldIntake.recording.reosKnows', { locale: 'cs' })}</h3>
            <section className="rh-v4-group">
              {context.known.map((fact) => (
                <div key={fact.factKey} className="rh-v4-row rh-v4-row--static">
                  <span className="rh-v4-row__text">
                    <span className="rh-v4-row__title">{fieldLabel(fact.factKey)}</span>
                  </span>
                  <span className="rh-v4-row__value">{formatValue(fact.factKey, fact.value)}</span>
                </div>
              ))}
            </section>
          </>
        ) : null}
        {/*
          Krok NAB1: „Z náboru" = hodnoty, ktere makler na miste zapsal sam.
          Zona 3 tim rika „co uz je vyplneno" i pred rozborem nahravky.
        */}
        {Object.values(marks).some((mark) => mark.value !== undefined) ? (
          <>
            <h3 className="rh-fi-h">{t('webAdmin.fieldIntake.recording.fromIntake', { locale: 'cs' })}</h3>
            <section className="rh-v4-group" data-testid="fi-marked-values">
              {Object.values(marks)
                .filter((mark) => mark.value !== undefined)
                .map((mark) => (
                  <div key={mark.factKey} className="rh-v4-row rh-v4-row--static">
                    <span className="rh-v4-row__text">
                      <span className="rh-v4-row__title">{fieldLabel(mark.factKey)}</span>
                    </span>
                    <span className="rh-v4-row__value">{formatValue(mark.factKey, mark.value)}</span>
                  </div>
                ))}
            </section>
          </>
        ) : null}
      </div>
    </div>
  );
}
